import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../../abstract/Document';
import type { ISupplier_Type } from '../Suppliers.types';
import { type Wedding, Weddings } from '../Weddings';
import type { ISupplier } from './Suppliers.types';
import { type QueryConstraint, where } from 'firebase/firestore';

@Identifiable
@Timestampable
@Untrackable
export class Supplier extends AbstractDocument<ISupplier> {
  readonly collections = {};
}

export class Suppliers extends AbstractCollection<Supplier, ISupplier> {
  static path = 'suppliers';

  constructor(document: Wedding) {
    super(document.collection(Suppliers.path), Supplier);
  }

  get wedding() {
    return Weddings._.getById(this.reference.parent!.id);
  }

  /**
   * Create supplier or update a supplier, depending on whether or not it already exists.
   */
  async add(data: ISupplier, book: boolean = false) {
    const supplier = this.getById(data.id);

    if ((await supplier.get()) == null) {
      return supplier.create(data);
    }

    return supplier.set(data, book ? ['booked'] : true);
  }

  /**
   * Get all wedding suppliers.
   *
   * @param type Supplier type, optional.
   * @param custom Whether to return only listed or custom suppliers, optional. Returns everything by default.
   */
  getAll(type?: ISupplier_Type, custom?: boolean) {
    const constraints: QueryConstraint[] = [];

    if (type != null) {
      constraints.push(where('type', '==', type));
    }

    if (custom != null) {
      constraints.push(where('customSupplier', '==', custom));
    }

    return this.query(constraints).get();
  }

  /**
   * Get all suppliers that have been ignored, including custom suppliers.
   *
   * @param type Supplier type, optional.
   */
  getIgnored(type?: ISupplier_Type) {
    if (type == null) {
      return this.query([where('ignored', '==', true)]).get();
    }

    return this.query([where('type', '==', type), where('ignored', '==', true)]).get();
  }

  /**
   * Get all suppliers that have been shortlisted, including custom suppliers.
   *
   * @param type Supplier type, optional.
   */
  getShortlisted(type?: ISupplier_Type) {
    if (type == null) {
      return this.query([where('shortlisted', '==', true)]).get();
    }

    return this.query([where('type', '==', type), where('shortlisted', '==', true)]).get();
  }
}
